import { CalculateStore } from "../../stores/calculateStore";
import { Risk, RiskModel, RisksId } from "../../helpers/risk";
import Formatter from "../../helpers/formatter";
import {
  DirectDiscountValues,
  OtherClaimlessPolicyDiscountTypes,
  SelectedValuesDiscountModel
} from "../../helpers/discount";
import { ClientModel } from "../../helpers/client";
import { ClientStore } from "../../stores/clientStore";
import { PaymentModeId } from "../../helpers/additionalQuestions/paymentMode";
import { PolicyDurationId } from "../../helpers/additionalQuestions/policyDuration";
import { PackageModel } from "../../helpers/package";
import { AdditionalQuestionsModel } from "../../helpers/additionalQuestions/additionalQuestions";
import { AddressModel, SimpleAddressClass } from "../../helpers/address";
import { EstateModel } from "../../helpers/estate";
import { NumberOfDamagesInLastThreeYearsId } from "../../helpers/additionalQuestions/numberOfDamagesInLastThreeYears";
import { ClaimlessYearsId } from "../../helpers/additionalQuestions/claimlessYearsIn5Years";
import { RisksNetworkTypes } from "../../helpers/risksNetwork";
import { PolicyStore } from "../../stores/policyStore";
import { RequestMapper } from "../../helpers/requestMapper";
import { UiFlagsStore } from "../../stores/uiFlagsStore";

export interface ResponsePartial {
  paymentMode: any;
  startDate: any;
  insuranceDuration: any;
  riskMode: any;
  corrections: any;
  additionalClauses?: any;
}

export interface PrepareRequestForPackageResponse {
  estate: any;
  selectedPackage: any;
  insurance: any;
  clients: any;
  courseOfInsurance: any;
  skipCalculationRegistration: any;
  policyRenewal: any;
  requestSource: string;
}

export interface CalculationRequestMapper {
  prepareRequestForPackage: (packageData: PackageModel, isNewBiznesForce: boolean) => any;
  prepareRequestForSummaryBox: (packageData: PackageModel, skipCalculationRegistration?: boolean) => any;
}

export abstract class DefaultCalculationRequestMapperBase implements CalculationRequestMapper {
  public constructor(protected calculateStore: CalculateStore, protected clientStore: ClientStore
    , protected policyStore: PolicyStore, protected uiFlagsStore: UiFlagsStore) {
  }

  public prepareRequestForPackage(packageData: PackageModel, isNewBiznesForce: boolean) {
    const estate = this.mapEstate(this.calculateStore.estate);
    const additionalQuestions = this.calculateStore.additionalQuestions;

    const insurance = {
      ...this.prepareOtherInsuranceValue(packageData, isNewBiznesForce),
      ...this.addSecondOpinionStandard()
    };

    const clients = [...this.prepareClientsData()];

    this.mapRequest(estate, insurance, additionalQuestions, packageData, this.calculateStore.propertyInsurance, isNewBiznesForce);

    const response: PrepareRequestForPackageResponse = {
      estate: this.calculateStore.propertyInsuranceOrOcChecked ? estate : null,
      selectedPackage: packageData.id.toUpperCase(),
      insurance,
      clients,
      courseOfInsurance: this.prepareCourseOfInsurance(),
      skipCalculationRegistration: true,
      policyRenewal: isNewBiznesForce ? undefined : this.policyRenewal(),
      requestSource: this.mapRequestSource()
    };
    if (isNewBiznesForce) {
      delete response.policyRenewal;
    }
    return response;
  }


  public prepareRequestForSummaryBox(packageData: PackageModel, skipCalculationRegistration: boolean = true) {
    const estate = this.mapEstate(this.calculateStore.estate);
    const additionalQuestions = this.calculateStore.additionalQuestions;

    const insurance = {
      ...this.prepareOtherInsuranceValue(packageData)
    };
    const clients = [...this.prepareClientsData()];

    this.mapRequest(estate, insurance, additionalQuestions, packageData, this.calculateStore.propertyInsurance);

    this.addAdditionalInsurancesToRequest(insurance);

    return {
      estate: this.calculateStore.propertyInsuranceOrOcChecked ? estate : null,
      selectedPackage: packageData.id.toUpperCase(),
      insurance,
      clients,
      courseOfInsurance: this.prepareCourseOfInsurance(),
      skipCalculationRegistration,
      apk: this.mapApk(),
      policyRenewal: this.policyRenewal(),
      parentCalculationId: this.calculateStore.parentCalculationId,
      requestSource: this.mapRequestSource(),
      apkUuid: this.calculateStore.apkUuid,
      isExternalApk: this.calculateStore.externalApk
    };
  }


  private mapRequestSource(): string {
    return this.isClientClickedRemoteSales() ? "SPRZEDAZZDALNA" : this.getRequestSourceByBirectOrSobol();
  }

  private getRequestSourceByBirectOrSobol(): string {
    return this.uiFlagsStore.isDirect ? "DIRECT" : "SOBOLv2";
  }

  private isClientClickedRemoteSales(): boolean {
    return this.uiFlagsStore.isClientEndsSelected;
  }

  private mapEstate(estate: EstateModel) {
    return {
      address: this.mapAddress(estate.address),
      area: estate.area.value,
      constructionYear: estate.constructionYear.value,
      flammableBuildingStructure: estate.flammableBuildingStructure,
      floor: estate.floor,
      overhaulYear: estate.overhaulYear.value,
      ownershipType: estate.ownershipType.value,
      propertyType: estate.propertyType
    };
  }

  private mapAddress(address: AddressModel) {
    return {
      apartmentNumber: address.formattedApartmentNumber(),
      buildingNumber: address.buildingNumber.value,
      city: address.city.value,
      county: address.county.value,
      street: address.street.value,
      zipCode: address.zipCode.value
    };
  }

  private mapSimpleAddress(address: SimpleAddressClass) {
    return {
      buildingAndApartmentNumber: address.buildingAndApartmentNumber.value,
      city: address.city.value,
      street: address.street.value,
      zipCode: address.zipCode.value
    };
  }

  private mapRequest(estate: any, insurance: any, additionalQuestions: AdditionalQuestionsModel, packageData: PackageModel, isPropertyInsurance: boolean, isNewBiznesForce = false) {
    this.mapPartOfFarm(estate, additionalQuestions);
    this.mapBusinessActivity(estate, additionalQuestions);
    RequestMapper.mapBusinessActivityType(estate, additionalQuestions);

    if (isPropertyInsurance) {
      this.addPackageDataToInsurance(insurance, packageData, isNewBiznesForce);
      this.mapAdditionalProtections(estate, additionalQuestions);
    } else {
      this.mapDefaultAdditionalProtections(estate);
    }
  }

  private addAdditionalInsurancesToRequest(insurance: any) {
    Object.assign(insurance, { ...this.prepareAdditionalInsurances() });
  }

  private addSecondOpinionStandard() {
    const secondOpinion = {};
    const additionalInsurances = this.calculateStore.additionalInsurances;

    this.addInsurance(additionalInsurances.secondOpinion, secondOpinion, {
      standard: true
    });

    if (!secondOpinion.hasOwnProperty("standard")) {
      Object.assign(secondOpinion, { standard: false });
    }

    return { secondOpinion };
  }

  private addPackageDataToInsurance(insurance: any, packageData: PackageModel, isNewBiznesForce = false) {
    Object.assign(insurance, { ...this.preparePackageRisks(packageData, isNewBiznesForce) });
  }

  private mapDefaultAdditionalProtections(estate: any) {
    Object.assign(estate, {
      additionalProtections: {
        antiburglarDoors: false,
        caretaker: false,
        antiburglarInstallation: false,
        firefightingInstallation: false,
        fireExtinguishing: false,
        securityWindows: false
      }
    });
  }

  private mapAdditionalProtections(estate: any, additionalQuestions: AdditionalQuestionsModel) {
    Object.assign(estate, {
      additionalProtections: { ...additionalQuestions.additionalProtections }
    });
  }

  private prepareAdditionalInsurances() {
    const additionalInsurances = this.calculateStore.additionalInsurances;
    const catAndDog = additionalInsurances.catAndDog;
    const ocAddress = this.mapSimpleAddress(additionalInsurances.ocAdditionalProperty.address);
    const preparedAdditionalInsurances = {};
    const nnw = {};
    const ocInPrivateLife = {};
    const secondOpinion = {};

    this.addInsurance(additionalInsurances.nonResidentialBuilding, preparedAdditionalInsurances, {
      nonResidentialBuildings: {
        insuranceSumInCents: Formatter.convertNormalToCentsNumber(additionalInsurances.nonResidentialBuilding.insuranceSum),
        flammableStructure: additionalInsurances.nonResidentialBuilding.flammableStructure
      }
    });

    this.addInsurance(additionalInsurances.fixedEstateElements, preparedAdditionalInsurances, {
      propertyFixedElements: {
        insuranceSumInCents: Formatter.convertNormalToCentsNumber(additionalInsurances.fixedEstateElements.insuranceSum)
      }
    });

    this.addInsurance(additionalInsurances.greenEnergySources, preparedAdditionalInsurances, {
      greenEnergySources: {
        insuranceSumInCents: Formatter.convertNormalToCentsNumber(additionalInsurances.greenEnergySources.insuranceSum)
      }
    });

    this.addInsurance(catAndDog, preparedAdditionalInsurances, {
      catAndDog: {
        catsCount: Number(catAndDog.catsCount),
        dogsCount: Number(catAndDog.dogsCount),
        insuranceSum: catAndDog.insuranceSum
      }
    });

    this.addInsurance(additionalInsurances.safeSenior, preparedAdditionalInsurances, {
      safeSenior: {
        numberOfInsureds: Number(additionalInsurances.safeSenior.safeSeniorCount)
      }
    });

    this.addInsurance(additionalInsurances.ocStandard, ocInPrivateLife, {
      guaranteeSum: additionalInsurances.ocStandard.ocGuaranteeSum
    });
    this.addInsurance(additionalInsurances.ocAdditionalProperty, ocInPrivateLife, {
      additionalProperty: {
        address: ocAddress
      }
    });
    this.addInsurance(additionalInsurances.ocChildExtension, ocInPrivateLife, {
      childExtension: true
    });
    this.addInsurance(additionalInsurances.ocRenterExtension, ocInPrivateLife, {
      renterExtension: true
    });
    if (Object.keys(ocInPrivateLife).length !== 0) {
      Object.assign(preparedAdditionalInsurances, { ocInPrivateLife });
    }

    this.addInsurance(additionalInsurances.legalProtection, preparedAdditionalInsurances, {
      legalProtection: { variant: additionalInsurances.legalProtection.variant }
    });

    this.addInsurance(additionalInsurances.nnwType, nnw, {
      numberOfClients: Number(additionalInsurances.nnwType.numberOfClients),
      insuranceSum: additionalInsurances.nnwType.insuranceSum
    });
    this.addInsurance(additionalInsurances.nnwSenior, nnw, {
      numberOfClientsAbove60years: Number(additionalInsurances.nnwSenior.nnwNumberOfSeniors)
    });
    this.addInsurance(additionalInsurances.nnwHeartAttackAndStroke, nnw, {
      extendedForHeartAttackAndStroke: Number(additionalInsurances.nnwHeartAttackAndStroke.numberOfClients)
    });
    if (Object.keys(nnw).length !== 0) {
      Object.assign(preparedAdditionalInsurances, { nnw });
    }

    this.addInsurance(additionalInsurances.gravestone, preparedAdditionalInsurances, {
      gravestone: {
        insuranceSum: additionalInsurances.gravestone.insuranceSum
      }
    });

    this.addInsurance(additionalInsurances.secondOpinion, secondOpinion, {
      standard: true
    });
    this.addInsurance(additionalInsurances.secondOpinionExtension, secondOpinion, {
      familyExtension: true
    });
    if (Object.keys(secondOpinion).length !== 0) {
      Object.assign(preparedAdditionalInsurances, { secondOpinion });
    }

    return preparedAdditionalInsurances;
  }

  private addInsurance(insurance: any, preparedAdditionalInsurances: any, insuranceValue: any) {
    if ((insurance.checked && insurance.risksNetworkValue !== RisksNetworkTypes.notAvailable) || insurance.risksNetworkValue === RisksNetworkTypes.required) {
      Object.assign(preparedAdditionalInsurances, insuranceValue);
    }
  }

  private preparePackageRisks(packageData: PackageModel, isNewBiznesForce = false) {
    const risks = packageData.risks;

    return {
      additionalRisks: {
        buildingToolsAndMaterials: Risk.getRiskInPackageValue(RisksId.buildingToolsAndMaterials, risks),
        devastation: this.preparePackageRiskWithInsuranceSum(RisksId.devastation, risks),
        searchingAndRemovingCauseOfDamage: Risk.getRiskInPackageValue(RisksId.searchingAndRemovingCauseOfDamage, risks),
        surges: this.preparePackageRiskWithInsuranceSum(RisksId.surges, risks),
        theft: Risk.getRiskInPackageValue(RisksId.theft, risks),
        windowpanes: this.preparePackageRiskWithInsuranceSum(RisksId.windowpanes, risks)
      },
      assistance: Risk.getRiskInPackageValue(RisksId.assistance, risks),
      burglaryAndTheftMovables: this.preparePackageRiskWithInsuranceSum(RisksId.burglaryAndTheftMovables, risks),
      detachedHouseOrFlatWithFixedElements: this.preparePackageRiskWithInsuranceSum(RisksId.detachedHouseOrFlatWithFixedElements, risks, isNewBiznesForce),
      fixedElementsWithBurglaryAndTheft: this.preparePackageRiskWithInsuranceSum(RisksId.fixedElementsWithBurglaryAndTheft, risks),
      flood: Risk.getRiskInPackageValue(RisksId.flood, risks),
      movables: this.preparePackageRiskWithInsuranceSum(RisksId.movables, risks),
      onlyFixedElements: this.preparePackageRiskWithInsuranceSum(RisksId.onlyFixedElements, risks)
    };
  }

  private preparePackageRiskWithInsuranceSum(riskId: RisksId, risks: Map<RisksId, RiskModel>, isNewBiznesForce = false) {
    if (Risk.getRiskInPackageValue(riskId, risks)) {
      const insuranceSum = Risk.getRiskInPackageInsuranceSum(riskId, risks);
      if (insuranceSum) {
        if (isNewBiznesForce) {
          return {};
        } else {
          return {
            insuranceSumInCents: Formatter.convertNormalToCentsNumber(insuranceSum)
          };
        }
      } else {
        return {};
      }
    } else {
      return undefined;
    }
  }

  private prepareOtherInsuranceValue(packageData: PackageModel, isNewBiznesForce = false) {
    const additionalQuestions = this.calculateStore.additionalQuestions;
    const discounts = this.calculateStore.discounts;

    const response: ResponsePartial = {
      paymentMode: this.preparePaymentMode(),
      startDate: Formatter.getStringDate(new Date(isNewBiznesForce ? this.addOneDay() : additionalQuestions.startDate)),
      insuranceDuration: this.prepareInsuranceDuration(),
      riskMode: Risk.getRiskMode(packageData?.risks),
      corrections: { ...this.prepareCorrections(discounts) },
      additionalClauses: isNewBiznesForce ? undefined : this.calculateStore.additionalClauses
    };

    if (isNewBiznesForce) {
      delete response.additionalClauses;
    }
    return response;
  }

  private addOneDay(date = new Date()) {
    date.setDate(date.getDate() + 1);
    return date;
  }

  private prepareClientsData() {
    const clients: any[] = [];
    this.clientStore.clients.forEach((client: ClientModel) => {
      clients.push(this.prepareClientData(client));
    });

    return clients;
  }

  private prepareClientData(clientData: ClientModel) {
    const client = {};
    const person = {};
    const company = {};

    Object.assign(client, { legalGroup: clientData.legalGroup });
    Object.assign(client, { insurer: clientData.insurer });
    Object.assign(client, { insured: clientData.insured });

    this.addNotEmptyClientData(person, "pesel", clientData.peselForRequest);
    this.addNotEmptyClientData(company, "regon", clientData.regonForRequest);
    this.addNotEmptyClientData(company, "nip", clientData.nipForRequest);

    this.addNotEmptyClientData(client, "person", person);
    this.addNotEmptyClientData(client, "company", company);

    return client;
  }

  private prepareCorrections(discounts: SelectedValuesDiscountModel) {
    const otherClaimlessPolicyDiscount = discounts.otherClaimlessPolicyDiscount.value.type;
    const managerDiscountValue = this.uiFlagsStore!.isDurAgent ? discounts.durManagerDiscount.value : discounts.managerDiscount.value;

        return {
            agentDiscount: this.prepareAgentDiscount(discounts.agentDiscount.value),
            otherClaimlessPolicy: otherClaimlessPolicyDiscount === OtherClaimlessPolicyDiscountTypes.empty ? null : otherClaimlessPolicyDiscount,
            managerDiscountInPercent: this.prepareManagerDiscount(managerDiscountValue),
            individualRiskAssessmentRaiseInPercent: Number(discounts.individualRiskAssessment.value),
            employeeDiscount: discounts.employeeDiscount.value,
            specialDiscountInPercent: Number(discounts.specialDiscount.value),
            dedicatedDiscountCode: discounts.dedicatedDiscountCode.value,
            nnwDiscountInPercent: Number(discounts.nnwDiscount.value)
        };
    }

  private prepareAgentDiscount(discount: string) {
    return this.uiFlagsStore!.isDirect
      ? DirectDiscountValues.FIVE_PERCENT
      : discount;
  }

    private prepareManagerDiscount(discount: string) {
        //default discount from Direct is 45% - 5% from AgentDiscount is combined with 40% ManagerDiscount
        const defaultDirectDiscount= 40;

        return this.uiFlagsStore!.isDirect
            ? defaultDirectDiscount
            : Number(discount);
    }

    private addNotEmptyClientData(obj: any, field: string, value: string | undefined | object) {
        if ((typeof value !== "object" && value) || (typeof value === "object" && Object.keys(value).length)) {
            Object.assign(obj, { [field]: value });
        }
    }

  private mapApk() {
    //TEMP TO DO FIX
    if (this.calculateStore.externalApkNoAndClientConsentNo)
      return {
        onlyHouse: true
      }
    else
      return {
        propertyInsurance: this.calculateStore.getAnswerOldModelValue('propertyInsurance'),
        oc: this.calculateStore.getAnswerOldModelValue('oc'),
        nnw: this.calculateStore.getAnswerOldModelValue('nnw'),
        businessActivity: this.calculateStore.getAnswerOldModelValue('businessActivity'),
        businessActivityInGarageOrFarmBuilding: this.calculateStore.getAnswerOldModelValue('businessActivityInGarageOrFarmBuilding'),
        availableBusinessActivity: this.calculateStore.getAnswerOldModelValue('availableBusinessActivity'),
        businessActivityArea: this.calculateStore.getAnswerOldModelValue('businessActivityArea'),
        farm: this.calculateStore.getAnswerOldModelValue('farm'),
        onlyHouse: this.calculateStore.getAnswerOldModelValue('onlyHouse'),
      };
  }

  private policyRenewal() {
    return this.policyStore.isPolicyRenewal ? {
      series: this.policyStore.policyRenewal.series,
      number: this.policyStore.policyRenewal.number.value
    } : undefined;
  }

  protected abstract prepareCourseOfInsurance(): {
    claimlessYearsIn5Years: ClaimlessYearsId | undefined,
    numberOfDamagesInLastThreeYears: NumberOfDamagesInLastThreeYearsId | undefined
  };

  protected abstract preparePaymentMode(): PaymentModeId | undefined;

  protected abstract prepareInsuranceDuration(): PolicyDurationId | undefined;

  protected abstract mapPartOfFarm(estate: any, additionalQuestions: AdditionalQuestionsModel): void;

  protected abstract mapBusinessActivity(estate: any, additionalQuestions: AdditionalQuestionsModel): void;
}


export class DefaultCalculationRequestMapper extends DefaultCalculationRequestMapperBase {
  // claimlessYearsIn5Years, numberOfDamagesInLastThreeYears, paymentMode and insuranceDuration are required
  // in /calculation request, so we send default values if form doesn't have these values

  protected prepareCourseOfInsurance() {
    const additionalQuestions = this.calculateStore.additionalQuestions;

    return {
      claimlessYearsIn5Years: additionalQuestions.claimlessYearsIn5Years.value || ClaimlessYearsId.zero,
      numberOfDamagesInLastThreeYears: additionalQuestions.numberOfDamagesInLastThreeYears.value || NumberOfDamagesInLastThreeYearsId.zero
    };
  }

  protected preparePaymentMode() {
    return this.calculateStore.additionalQuestions.paymentMode.value || PaymentModeId.oneTime;
  }

  protected prepareInsuranceDuration() {
    return this.calculateStore.additionalQuestions.policyDuration.value || PolicyDurationId.MONTHS_12;
  }

  protected mapPartOfFarm(estate: any, additionalQuestions: AdditionalQuestionsModel) {
    RequestMapper.mapPartOfFarm(estate, additionalQuestions);
  }

  protected mapBusinessActivity(estate: any, additionalQuestions: AdditionalQuestionsModel) {
    RequestMapper.mapBusinessActivity(estate, additionalQuestions);
  }


}
